import {
    Bars2Icon,
    ChevronLeftIcon,
    ChevronRightIcon,
    DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react';

const DEFAULT_EDITOR_WIDTH = 62;
const MIN_EDITOR_WIDTH = 48;
const MAX_EDITOR_WIDTH = 72;
const DESKTOP_BREAKPOINT = 1024;

type DocumentWorkspaceProps = {
    editor: React.ReactNode;
    preview: React.ReactNode;
    fileName?: string;
    mimeType?: string;
};

function clampEditorWidth(width: number) {
    return Math.min(MAX_EDITOR_WIDTH, Math.max(MIN_EDITOR_WIDTH, width));
}

function getSourceTypeLabel(mimeType?: string, fileName?: string) {
    if (mimeType === 'application/pdf') return 'PDF';
    if (mimeType?.includes('xml')) return 'UBL / XML';
    if (mimeType?.startsWith('image/')) return 'Afbeelding';

    const extension = fileName?.split('.').pop();
    return extension && extension !== fileName ? extension.toUpperCase() : 'Bestand';
}

export function DocumentWorkspace({
    editor,
    preview,
    fileName,
    mimeType,
}: DocumentWorkspaceProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [editorWidth, setEditorWidth] = useState(DEFAULT_EDITOR_WIDTH);
    const [isPreviewCollapsed, setIsPreviewCollapsed] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const dragWidthRef = useRef(DEFAULT_EDITOR_WIDTH);
    const sourceTypeLabel = getSourceTypeLabel(mimeType, fileName);

    const widthFromClientX = (clientX: number) => {
        const bounds = containerRef.current?.getBoundingClientRect();
        if (!bounds || bounds.width < DESKTOP_BREAKPOINT) return null;
        return clampEditorWidth(((clientX - bounds.left) / bounds.width) * 100);
    };

    const applyDragWidth = (width: number) => {
        dragWidthRef.current = width;
        containerRef.current?.style.setProperty('--editor-width', `${width}%`);
    };

    const handleResizeKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Home') {
            event.preventDefault();
            setEditorWidth(MIN_EDITOR_WIDTH);
        } else if (event.key === 'End') {
            event.preventDefault();
            setEditorWidth(MAX_EDITOR_WIDTH);
        } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            setEditorWidth((current) => clampEditorWidth(current - 2));
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            setEditorWidth((current) => clampEditorWidth(current + 2));
        }
    };

    const startResizing = (event: PointerEvent<HTMLDivElement>) => {
        if (event.button !== 0 || window.innerWidth < DESKTOP_BREAKPOINT) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        dragWidthRef.current = editorWidth;
        setIsResizing(true);
    };

    const resize = (event: PointerEvent<HTMLDivElement>) => {
        if (!isResizing) return;
        const width = widthFromClientX(event.clientX);
        if (width !== null) applyDragWidth(width);
    };

    const stopResizing = () => {
        if (!isResizing) return;
        setIsResizing(false);
        setEditorWidth(dragWidthRef.current);
    };

    const editorStyle = { '--editor-width': `${editorWidth}%` } as CSSProperties;

    return (
        <div
            ref={containerRef}
            style={editorStyle}
            className="flex min-h-0 w-full flex-1 flex-col overflow-hidden lg:flex-row"
        >
            <section
                aria-label="Documentgegevens"
                className={isPreviewCollapsed
                    ? 'flex min-h-0 min-w-0 flex-1 flex-col bg-white'
                    : 'flex min-h-0 min-w-0 flex-1 flex-col bg-white lg:flex-none lg:basis-[var(--editor-width)]'}
            >
                {editor}
            </section>

            {!isPreviewCollapsed && (
                <div
                    role="separator"
                    aria-label="Breedte van het brondocument aanpassen"
                    aria-orientation="vertical"
                    aria-valuemin={MIN_EDITOR_WIDTH}
                    aria-valuemax={MAX_EDITOR_WIDTH}
                    aria-valuenow={Math.round(editorWidth)}
                    tabIndex={0}
                    onPointerDown={startResizing}
                    onPointerMove={resize}
                    onPointerUp={stopResizing}
                    onPointerCancel={stopResizing}
                    onLostPointerCapture={stopResizing}
                    onDoubleClick={() => setEditorWidth(DEFAULT_EDITOR_WIDTH)}
                    onKeyDown={handleResizeKeyDown}
                    className="group relative z-10 hidden w-2 shrink-0 cursor-col-resize touch-none items-center justify-center bg-gray-100 outline-none lg:flex focus-visible:bg-primary/10"
                    title="Sleep om de breedte aan te passen. Dubbelklik om te herstellen."
                >
                    <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-gray-200 transition-colors group-hover:bg-primary group-focus-visible:bg-primary" />
                    <span className="relative rounded border border-gray-200 bg-white py-2 text-gray-400 shadow-sm transition-colors duration-150 group-hover:border-primary/40 group-hover:text-primary group-focus-visible:border-primary group-focus-visible:text-primary">
                        <Bars2Icon className="h-4 w-4 rotate-90" aria-hidden="true" />
                    </span>
                </div>
            )}

            <aside
                aria-label="Brondocument"
                className={isPreviewCollapsed
                    ? 'flex h-11 shrink-0 flex-col border-t border-gray-200 bg-gray-50 lg:h-auto lg:w-12 lg:border-l lg:border-t-0'
                    : 'flex h-[42%] min-h-[18rem] shrink-0 flex-col border-t border-gray-200 bg-gray-100 lg:h-auto lg:min-h-0 lg:min-w-[20rem] lg:flex-1 lg:border-l-0 lg:border-t-0'}
            >
                {isPreviewCollapsed ? (
                    <button
                        type="button"
                        onClick={() => setIsPreviewCollapsed(false)}
                        className="flex h-full w-full items-center justify-center gap-2 text-xs font-semibold text-gray-600 transition-colors duration-150 hover:bg-white hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary lg:flex-col"
                        aria-label="Brondocument tonen"
                        title="Brondocument tonen"
                    >
                        <ChevronLeftIcon className="hidden h-4 w-4 lg:block" aria-hidden="true" />
                        <DocumentTextIcon className="h-4 w-4" aria-hidden="true" />
                        <span className="lg:[writing-mode:vertical-rl] lg:rotate-180">Bron tonen</span>
                    </button>
                ) : (
                    <>
                        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-gray-200 bg-white px-3">
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500">
                                <DocumentTextIcon className="h-4 w-4" aria-hidden="true" />
                            </span>
                            <div className="min-w-0 flex-1">
                                <div className="flex min-w-0 items-center gap-2">
                                    <h2 className="shrink-0 text-xs font-semibold uppercase tracking-wide text-gray-500">Bron</h2>
                                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                                        {sourceTypeLabel}
                                    </span>
                                </div>
                                <p className="truncate text-xs font-medium text-gray-800" title={fileName}>
                                    {fileName || 'Documentbestand'}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsPreviewCollapsed(true)}
                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 transition-[background-color,color,transform] duration-150 hover:bg-gray-50 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary active:scale-[0.97]"
                                aria-label="Brondocument inklappen"
                                title="Brondocument inklappen"
                            >
                                <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
                            </button>
                        </header>
                        <div className="min-h-0 flex-1 overflow-hidden">{preview}</div>
                    </>
                )}
            </aside>

            {isResizing && <div aria-hidden="true" className="fixed inset-0 z-50 cursor-col-resize select-none" />}
        </div>
    );
}
